import { getApiBaseUrl } from "../config/runtimeConfig";

const apiBaseUrl = getApiBaseUrl();

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export interface CommentAuthor {
  name: string;
  avatarUrl?: string | null;
  isSystem: boolean;
}

export interface CommentAttachment {
  id: number;
  fileName: string;
  contentType: string;
  sizeInBytes: number;
  url?: string | null;
}

export interface Comment {
  id: number;
  contextType: string;
  contextId: number;
  content: string;
  isSystem: boolean;
  systemType?: string | null;
  author: CommentAuthor;
  attachments: CommentAttachment[];
  created: string;
  canEdit: boolean;
}

export interface CreateCommentPayload {
  contextType: string;
  contextId: number;
  content: string;
  isSystem?: boolean;
  systemType?: string | null;
  attachments?: CommentAttachmentPayload[];
}

export interface CommentAttachmentPayload {
  fileName: string;
  contentType: string;
  sizeInBytes: number;
  url?: string | null;
}

export interface CommentAttachmentUpload {
  id?: number;
  fileName: string;
  contentType: string;
  sizeInBytes: number;
  url?: string | null;
}

async function request<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(
      response.status,
      text || response.statusText,
    );
  }

  const text = await response.text();
  if (!text) {
    return undefined as T;
  }

  return JSON.parse(text) as T;
}

export function getComments(
  contextType: string,
  contextId: number,
): Promise<Comment[]> {
  const query = new URLSearchParams({
    contextType,
    contextId: contextId.toString(),
  });

  return request<Comment[]>(`/api/comments?${query.toString()}`);
}

export function createComment(
  payload: CreateCommentPayload,
): Promise<Comment> {
  return request<Comment>("/api/comments", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function updateComment(
  commentId: number,
  content: string,
): Promise<Comment> {
  return request<Comment>(`/api/comments/${commentId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ content }),
  });
}

export function deleteComment(commentId: number): Promise<void> {
  return request<void>(`/api/comments/${commentId}`, {
    method: "DELETE",
  });
}

export function uploadCommentAttachment(
  file: File,
): Promise<CommentAttachmentUpload> {
  const formData = new FormData();
  formData.append("attachment", file, file.name || "anexo");

  return request<CommentAttachmentUpload>("/api/comments/attachments", {
    method: "POST",
    body: formData,
  });
}

export const getCommentAttachmentDownloadUrl = (
  commentId: number,
  attachmentId: number,
): string => {
  return `${apiBaseUrl}/api/comments/${commentId}/attachments/${attachmentId}/download`;
};
